/**
 * Email settings — the branded chrome, the images, and proof that the mail server works.
 *
 * Three panels, in the order a deployment is set up: decide what the mail looks like, load the
 * images it uses, and prove a message can actually leave the building. Superadmin only (the
 * route guard and the API both enforce).
 *
 * ## Why the SMTP settings are not edited here
 *
 * They are provider credentials and they live on the Provider keys page with every other vendor
 * secret, under the same reveal rules and the same audit trail. Rebuilding them here would be a
 * second place a password could be typed, and a second place to get that wrong.
 *
 * What lives here is the **test**, because it is an email concern and because it is the step
 * that stops the failure this platform is worst at noticing. A wrong API key fails the next call
 * with a 401 somebody sees. A wrong SMTP port fails at a socket timeout thirty seconds later,
 * inside a worker, on a receipt nobody is watching, and the first symptom is a customer saying
 * they never got their code, days after the change that caused it. So the settings are sent
 * here, a real session is opened against them, a real message goes out, and the operator reads
 * the verdict. Nothing is saved by it.
 *
 * ## The password
 *
 * A stored SMTP password is never rendered into this form, in any shape. The console has no way
 * to obtain one: no read returns it. The test therefore omits `password` by default and the
 * server uses the stored one, which is what lets an operator test a port change without retyping
 * a secret they may not have. `buildSmtpTestRequest` is the only thing that constructs the body.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  EmailImage,
  EmailLayout,
  EmailLayoutContent,
  EmailLayoutVersion,
  PlatformSettings,
  SmtpTestResponse,
} from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, Field, Input, LoadingState, Select } from '../components/ui.js';
import { EmailImageLibrary } from '../components/EmailImageLibrary.js';
import { SecretInput } from '../components/SecretInput.js';
import { EmailLayoutEditor } from '../components/EmailLayoutEditor.js';
import { EmailPreview } from '../components/EmailPreview.js';
import { IconExternalLink, IconMail, IconSend, IconShield } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useBrand } from '../branding/BrandProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import {
  defaultEmailLayoutContent,
  renderWithLayout,
} from '../lib/emailLayout.js';
import {
  DEFAULT_PORTS,
  TLS_HINTS,
  TLS_LABELS,
  TLS_MODES,
  buildSmtpTestRequest,
  emptySmtpDraft,
  passwordStateLabel,
  validateSmtpDraft,
  type PasswordMode,
  type SmtpDraft,
  type TlsMode,
} from '../lib/smtpSettings.js';

interface Loaded {
  readonly settings: PlatformSettings;
  readonly layout: EmailLayout | null;
  readonly versions: readonly EmailLayoutVersion[];
  readonly images: readonly EmailImage[];
}

/** A short, honest sample so the chrome can be judged without a real template in the way. */
const SAMPLE_BODY = `<h1 style="margin:0 0 16px 0;font-size:22px;line-height:30px;font-weight:700;color:{{layoutHeading}};">A sample message</h1>
<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:{{layoutBody}};">This is what the body of a message looks like inside your layout. Headings, body text, a link and a button all use the colours you set.</p>
<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:{{layoutBody}};"><a href="https://example.com" style="color:{{layoutLink}};">Here is a link</a> in the middle of a sentence.</p>
<table role="presentation" cellpadding="0" cellspacing="0" style="margin:8px 0 20px;"><tr><td style="border-radius:10px;background:{{layoutButtonBg}};"><a href="https://example.com" style="display:inline-block;padding:12px 26px;font-size:15px;font-weight:600;color:{{layoutButtonText}};text-decoration:none;border-radius:10px;">A button</a></td></tr></table>
<p style="margin:16px 0 0 0;font-size:13px;line-height:20px;color:{{layoutMuted}};">And this is the small print, which is the line that most often turns out to be unreadable.</p>`;

export function EmailSettingsPage(): JSX.Element {
  const { api } = useAuth();

  const load = useCallback(async (): Promise<Loaded> => {
    const [settings, layout, images] = await Promise.all([
      api.getBusinessSettings(),
      api.getEmailLayout(),
      api.listEmailImages(),
    ]);
    // The history is a nicety, not a requirement: a deployment that has never saved a layout has
    // none, and a failure here must not take the whole page down with it.
    let versions: readonly EmailLayoutVersion[] = [];
    try {
      versions = (await api.emailLayoutVersions()).versions;
    } catch {
      versions = [];
    }
    return { settings, layout, versions, images: images.images };
  }, [api]);

  const state = useAsync(load, [load]);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Email settings</h1>
        <p className="page-head__sub">
          The branded frame every message is poured into, the images it can use, and a way to
          prove the mail server works before it is trusted with real mail.
        </p>
      </div>

      {state.loading ? (
        <LoadingState label="Loading email settings…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="Email settings are not available." />
      ) : (
        <Panels
          data={state.data}
          onLayout={(layout) => state.setData((prev) => (prev === null ? prev : { ...prev, layout }))}
          onVersions={(versions) => state.setData((prev) => (prev === null ? prev : { ...prev, versions }))}
          onImages={(images) => state.setData((prev) => (prev === null ? prev : { ...prev, images }))}
        />
      )}
    </>
  );
}

function Panels({
  data,
  onLayout,
  onVersions,
  onImages,
}: {
  data: Loaded;
  onLayout: (next: EmailLayout) => void;
  onVersions: (next: readonly EmailLayoutVersion[]) => void;
  onImages: (next: readonly EmailImage[]) => void;
}): JSX.Element {
  const { session } = useAuth();
  const brand = useBrand();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');

  // The draft the editor is typing, so the preview beside it is the layout being edited rather
  // than the one last saved. Seeded from the stored layout, or from the shipped default.
  const [draft, setDraft] = useState<EmailLayoutContent>(
    data.layout?.content ?? defaultEmailLayoutContent(),
  );

  const preview = renderWithLayout(
    draft,
    {
      platformName: data.settings.platformName,
      logoUrl: brand.logoUrl,
      supportEmail: data.settings.supportEmail,
      websiteUrl: data.settings.websiteUrl,
    },
    { preheader: 'A sample message, so you can see the frame', bodyHtml: SAMPLE_BODY },
  );

  return (
    <div className="settings-hub">
      <div className="grid grid--2">
        <div className="settings-hub">
          <EmailLayoutEditor
            layout={data.layout}
            versions={data.versions}
            canWrite={canWrite}
            onSaved={onLayout}
            onVersions={onVersions}
            onDraft={setDraft}
          />
        </div>

        <div className="settings-hub">
          <Card title="Preview" flush>
            <EmailPreview
              subject="A sample message"
              preheader="A sample message, so you can see the frame"
              html={preview}
              senderName={draft.senderName === '' ? data.settings.platformName : draft.senderName}
              fromEmail={data.settings.supportEmail}
            />
          </Card>

          <SmtpTestPanel canWrite={canWrite} />
        </div>
      </div>

      <EmailImageLibrary
        images={data.images}
        canWrite={canWrite}
        onChange={onImages}
        hint="Uploaded once and reusable in any template or broadcast. The API serves them from a public address, because a mail client cannot sign a request."
      />

      <Card title="Where these are used">
        <div className="quicklinks">
          <Link to="/email-templates" className="quicklink">
            <span className="quicklink__icon">
              <IconMail size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">Email templates</span>
              <span className="quicklink__hint">Every transactional message, edited and previewed</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
          <Link to="/email-broadcast" className="quicklink">
            <span className="quicklink__icon">
              <IconSend size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">Email broadcast</span>
              <span className="quicklink__hint">Write to one customer, several, or a whole group</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
          <Link to="/providers/credentials" className="quicklink">
            <span className="quicklink__icon">
              <IconShield size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">Provider keys</span>
              <span className="quicklink__hint">Where the SMTP host, port and password are saved</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
        </div>
      </Card>
    </div>
  );
}

function SmtpTestPanel({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();

  const [draft, setDraft] = useState<SmtpDraft>(() => emptySmtpDraft());
  const [mode, setMode] = useState<PasswordMode>('stored');
  const [typed, setTyped] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<SmtpTestResponse | null>(null);

  function patch(next: Partial<SmtpDraft>): void {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  /**
   * Changing the encryption mode moves the port too, but only when the port is still the
   * convention for the mode being left. An operator who typed 2525 on purpose keeps it.
   */
  function changeTls(tlsMode: TlsMode): void {
    setDraft((prev) => ({
      ...prev,
      tlsMode,
      port: prev.port.trim() === DEFAULT_PORTS[prev.tlsMode] ? DEFAULT_PORTS[tlsMode] : prev.port,
    }));
  }

  async function run(): Promise<void> {
    const found = validateSmtpDraft(draft, { mode, typed });
    setProblems(found);
    if (found.length > 0) return;
    setTesting(true);
    setResult(null);
    try {
      const response = await api.smtpTest(buildSmtpTestRequest(draft, { mode, typed }));
      setResult(response);
      // The typed secret leaves this component the moment it has been sent. Keeping it in state
      // would be a password sitting in a React tree for the rest of the session.
      setTyped('');
      setMode('stored');
      if (response.delivered) toast.success('Test delivered', `Check ${draft.to.trim()}.`);
    } catch (err) {
      toast.error('Could not run the test', messageOf(err));
    } finally {
      setTesting(false);
    }
  }

  return (
    <Card title="Prove the mail server works">
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Sends one real message through these settings and reports what the server said. Nothing
        is saved here. The settings themselves live on{' '}
        <Link to="/providers/credentials" className="mailnote__link">
          Provider keys
        </Link>
        .
      </p>

      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label="Host" htmlFor="smtp-host">
          <Input
            id="smtp-host"
            value={draft.host}
            disabled={!canWrite}
            autoComplete="off"
            placeholder="smtp.yourprovider.com"
            onChange={(e) => patch({ host: e.target.value })}
          />
        </Field>
        <Field label="Port" htmlFor="smtp-port">
          <Input
            id="smtp-port"
            value={draft.port}
            inputMode="numeric"
            maxLength={5}
            disabled={!canWrite}
            className="mono"
            onChange={(e) => patch({ port: e.target.value })}
          />
        </Field>
      </div>

      <Field label="Encryption" htmlFor="smtp-tls">
        <Select
          id="smtp-tls"
          value={draft.tlsMode}
          disabled={!canWrite}
          onChange={(e) => changeTls(e.target.value as TlsMode)}
        >
          {TLS_MODES.map((m) => (
            <option key={m} value={m}>
              {TLS_LABELS[m]}
            </option>
          ))}
        </Select>
        <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
          {TLS_HINTS[draft.tlsMode]}
        </div>
      </Field>

      <Field label="Username" htmlFor="smtp-user">
        <Input
          id="smtp-user"
          value={draft.username}
          disabled={!canWrite}
          autoComplete="off"
          placeholder="Leave empty if the server needs no login"
          onChange={(e) => patch({ username: e.target.value })}
        />
      </Field>

      <div className="field">
        <span className="field__label">Password</span>
        <div className="smtp-secret">
          {mode === 'typed' ? (
            <>
              {/* Rendered only in this mode and starts empty. There is no code path that can
                  populate it from the server, because no read carries a password to populate
                  it with. */}
              <SecretInput
                fieldName="smtp-password"
                type="password"
                value={typed}
                disabled={!canWrite}
                placeholder="Type a password to test instead of the stored one"
                aria-label="SMTP password to test"
                onChange={(e) => setTyped(e.target.value)}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setTyped('');
                  setMode('stored');
                }}
              >
                Use the stored one
              </Button>
            </>
          ) : (
            <>
              <span className="smtp-secret__state">
                <IconShield size={15} />
                {passwordStateLabel(mode)}
              </span>
              <Button size="sm" variant="ghost" disabled={!canWrite} onClick={() => setMode('typed')}>
                Test a different one
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label="From address" htmlFor="smtp-from">
          <Input
            id="smtp-from"
            type="email"
            value={draft.from}
            disabled={!canWrite}
            placeholder="no-reply@yourdomain.com"
            onChange={(e) => patch({ from: e.target.value })}
          />
        </Field>
        <Field label="From name" htmlFor="smtp-fromname">
          <Input
            id="smtp-fromname"
            value={draft.fromName}
            maxLength={120}
            disabled={!canWrite}
            onChange={(e) => patch({ fromName: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label="Reply-to (optional)" htmlFor="smtp-replyto">
          <Input
            id="smtp-replyto"
            type="email"
            value={draft.replyTo}
            disabled={!canWrite}
            onChange={(e) => patch({ replyTo: e.target.value })}
          />
        </Field>
        <Field label="Send the test to" htmlFor="smtp-to">
          <Input
            id="smtp-to"
            type="email"
            value={draft.to}
            disabled={!canWrite}
            placeholder="you@example.com"
            onChange={(e) => patch({ to: e.target.value })}
          />
        </Field>
      </div>

      {problems.length > 0 && (
        <div className="form-error" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      {result !== null && (
        <div
          className={`banner-verdict ${result.delivered ? 'banner-verdict--ok' : 'banner-verdict--bad'}`}
          role={result.delivered ? 'status' : 'alert'}
        >
          <div className="banner-verdict__head">
            {result.delivered
              ? 'The server accepted the message.'
              : 'The server did not accept the message.'}
          </div>
          {result.message !== null && <div>{result.message}</div>}
          <div className="banner-verdict__ratios">
            {result.code !== null && <span>code {result.code}</span>}
            <span>{result.retryable ? 'trying again could work' : 'a setting needs changing'}</span>
          </div>
        </div>
      )}

      <div className="settings-actions" style={{ marginTop: 8 }}>
        {!canWrite && (
          <div className="muted" style={{ fontSize: 13 }}>
            Your role cannot run this test.
          </div>
        )}
        <Button variant="primary" loading={testing} disabled={!canWrite} onClick={() => void run()}>
          <IconSend size={15} /> Send a test message
        </Button>
      </div>

      {result !== null && !result.delivered && (
        <div style={{ marginTop: 10 }}>
          <Badge tone="amber" dot>
            Nothing was saved
          </Badge>
        </div>
      )}
    </Card>
  );
}
